import React from "react";
import { paymentAmount } from '../lib/depositTerms';
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Check, Calendar, MapPin, Mail, Loader2, Download } from "lucide-react";
import api from "../lib/api";
import Grain from "../components/Grain";

export default function ReservationSuccess() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [booking, setBooking] = React.useState(null);
  const [status, setStatus] = React.useState("polling"); // polling | paid | failed
  const [tries, setTries] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        if (sid) {
          const r = await api.get(`/bookings/payment-status/${sid}`);
          if (r.data.payment_status === "paid") {
            const b = await api.get(`/bookings/${bookingId}`);
            if (!cancelled) {
              setBooking(b.data);
              setStatus("paid");
            }
            return;
          }
        }
        const b = await api.get(`/bookings/${bookingId}`);
        if (b.data.status === "paid" || b.data.status === "confirmed") {
          if (!cancelled) {
            setBooking(b.data);
            setStatus("paid");
          }
          return;
        }
      } catch (_e) { /* ignore */ }
      if (!cancelled) {
        setTries(t => t + 1);
      }
    };
    poll();
    const interval = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(interval); };
  }, [sid, bookingId]);

  React.useEffect(() => {
    if (tries >= 12 && status === "polling") setStatus("failed");
  }, [tries, status]);

  if (status === "polling") {
    return (
      <div className="section-dark min-h-screen pt-32 pb-20 relative">
        <Grain />
        <div className="max-w-xl mx-auto px-6 text-center relative z-10">
          <Loader2 className="animate-spin text-accent-deep mx-auto mb-6" size={44} />
          <h1 className="font-serif text-3xl mb-3">Confirmation en cours...</h1>
          <p className="text-text-cream/60 font-light">Nous vérifions ton paiement avec Stripe. Cela prend quelques secondes.</p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="section-dark min-h-screen pt-32 pb-20 relative">
        <Grain />
        <div className="max-w-xl mx-auto px-6 text-center relative z-10">
          <h1 className="font-serif text-3xl mb-3">Confirmation en attente</h1>
          <p className="text-text-cream/60 font-light mb-6">
            Si tu as bien reçu la confirmation Stripe, ton paiement sera visible d'ici quelques minutes.
            Si tu as un doute, contacte-moi à <a href="mailto:studioeuclides25@gmail.com" className="text-accent-light underline">studioeuclides25@gmail.com</a>.
          </p>
          <Link to="/reservation" className="btn-primary inline-flex">Retour</Link>
        </div>
      </div>
    );
  }

  const date = booking?.date ? new Date(booking.date + "T00:00:00") : null;
  const dateFr = date ? date.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";
  const ref = (booking?.id || "").slice(-8).toUpperCase();

  return (
    <div className="section-dark min-h-screen pt-28 pb-20 relative">
      <Grain />
      <div className="max-w-2xl mx-auto px-6 relative z-10" data-testid="success-page">
        <div className="w-16 h-16 rounded-full bg-accent-deep/15 border border-accent-deep flex items-center justify-center mx-auto mb-6">
          <Check size={28} className="text-accent-light" />
        </div>
        <h1 className="font-serif text-4xl md:text-5xl text-center mb-3">
          Réservation <span className="italic-accent">confirmée</span>
        </h1>
        <p className="text-text-cream/60 text-center mb-10 font-light">
          Merci {booking.client_prenom} ! Tu reçois à l'instant un email avec tous les détails et un fichier calendrier (.ics).
        </p>

        <div className="bg-white/[0.03] border border-white/10 rounded p-6 space-y-5 mb-8">
          <div>
            <div className="eyebrow text-text-cream/40 mb-1">Référence</div>
            <div className="font-serif text-xl text-accent-light">#{ref}</div>
          </div>
          <Line icon={<Calendar size={14} />} label="Service" value={booking.service_name} />
          <Line icon={<Calendar size={14} />} label="Date" value={dateFr} />
          <Line icon={<Calendar size={14} />} label="Horaire" value={`${booking.start_time} – ${booking.end_time}`} />
          <Line icon={<MapPin size={14} />} label="Adresse" value="Studio Euclides, Route de Crochy 20, 1024 Écublens" />
          <Line icon={<Mail size={14} />} label="Confirmation" value={booking.client_email} />
        </div>

        <div className="p-5 bg-white/[0.03] border border-white/10 rounded mb-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-cream/60">Acompte payé</span>
            <span className="font-serif text-xl text-accent-light" data-testid="success-deposit-amount">CHF {paymentAmount(booking.deposit_chf)}</span>
          </div>
          {booking.remaining_chf > 0 && (
            <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-white/10">
              <span className="text-text-cream/60">Reste à régler sur place (cash ou TWINT)</span>
              <span className="text-text-cream" data-testid="success-remaining-amount">CHF {paymentAmount(booking.remaining_chf)}</span>
            </div>
          )}
          {booking.remaining_chf > 0 && booking.payment_terms_snapshot?.balance_payment_note && <p className="text-xs text-text-cream/70 mt-3" data-testid="success-balance-payment-note">{booking.payment_terms_snapshot.balance_payment_note}</p>}
        </div>

        <div className="text-center">
          <a href="https://www.google.com/maps/search/?api=1&query=Route+de+Crochy+20+1024+Ecublens" target="_blank" rel="noopener noreferrer" className="btn-ghost mr-3 inline-flex">
            <MapPin size={14} className="mr-2"/> Ouvrir dans Maps
          </a>
          <Link to="/" className="btn-primary inline-flex">Retour à l'accueil</Link>
        </div>
      </div>
    </div>
  );
}

function Line({ icon, label, value }) {
  return (
    <div className="flex gap-4">
      <div className="w-7 h-7 rounded-full bg-accent-deep/15 flex items-center justify-center text-accent-light flex-shrink-0">{icon}</div>
      <div className="flex-1">
        <div className="eyebrow text-text-cream/40 mb-0.5">{label}</div>
        <div className="text-text-cream font-light">{value}</div>
      </div>
    </div>
  );
}
